import React, { useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import type { LaunchableSkin } from "components/Launchables/DeployRedesign/shared/content/skins/registry";
import type { ProcessedLaunchableData } from "components/Launchables/utils";
import { launchableContentTabsListClassName } from "./LaunchableContentTab";
import LaunchableConfigurationContent from "./configuration/LaunchableConfigurationContent";
import FileViewer from "./overview/FileViewer";
import { SkinContent } from "components/Launchables/DeployRedesign/shared/content/skins/SkinContent";

interface LaunchableContentTabsProps {
  launchableData?: ProcessedLaunchableData | null;
  lifecycleScript?: string | null;
  activeSkin: LaunchableSkin | null;
}

export const LaunchableContentTabs: React.FC<LaunchableContentTabsProps> = ({
  launchableData,
  lifecycleScript,
  activeSkin,
}) => {
  const hasReadme = Boolean(launchableData?.file?.trim());
  const hasOverview = Boolean(activeSkin);
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const activeTab =
    selectedTab ??
    (hasReadme ? "readme" : hasOverview ? "overview" : "configuration");
  const readme = <FileViewer file={launchableData?.file} />;
  const configuration = (
    <LaunchableConfigurationContent
      file={launchableData?.file}
      lifecycleScript={lifecycleScript}
      containerSelected={launchableData?.containerSelected}
      imageId={launchableData?.imageId}
      ports={launchableData?.ports}
      firewallRules={launchableData?.firewallRules}
    />
  );
  const sharedSkinProps = {
    activeTab,
    onActiveTabChange: setSelectedTab,
    leadingTabs: hasReadme
      ? [{ value: "readme", label: "Overview", content: readme }]
      : [],
    trailingTabs: [
      {
        value: "configuration",
        label: "Configuration",
        content: configuration,
      },
    ],
  };

  if (activeSkin) {
    return (
      <div className="w-full">
        <SkinContent skin={activeSkin} {...sharedSkinProps} />
      </div>
    );
  }

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={setSelectedTab}>
        <TabsList
          aria-label="Launchable content"
          className={launchableContentTabsListClassName}
        >
          {hasReadme && <TabsTrigger value="readme">Overview</TabsTrigger>}
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
        </TabsList>
        {hasReadme && <TabsContent value="readme">{readme}</TabsContent>}
        <TabsContent value="configuration">{configuration}</TabsContent>
      </Tabs>
    </div>
  );
};
